<template>
  <section id="timebar" class="has-background-black-ter">
    <div>
      <div class="controls">
        <a @click="togglePlay">
          <b-icon :icon="icon" type="is-white"></b-icon>
        </a>
      </div>
      <div class="bar">
        <vue-slider
          v-model="current"
          contained="true"
          tooltip="always"
          :useKeyboard="true"
          :adsorb="true"
          :min="min"
          :max="max"
          :interval="runstep"
          :tooltipFormatter="val => toDate(val, 'DD.MM')"
          :dotOptions="dotOptions"
          :process="process"
          :marks="ticks"
          :processStyle="{ background: '#ff9800' }"
          :tooltipStyle="{ background: '#ff9800' }"
          @change="changed"
          ></vue-slider>
      </div>
    </div>
  </section>
</template>

<script>

import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import moment from 'moment'

const dateFormat = 'YYYY-MM-DD'

export default {
  components: {
    VueSlider
  },
  props: {
    minDate: String,
    maxDate: String,
    from: String,
    to: String,
    value: String,
    step: {
      type: Number,
      default: 1
    },
    milestones: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      min: moment.utc(this.minDate, dateFormat).unix(),
      max: moment.utc(this.maxDate, dateFormat).unix(),
      current: moment.utc(this.value, dateFormat).unix(),
      runstep: (this.step * 24 * 3600),
      markMinDistance: (12 * 24 * 3600),
      play: false,
      run: null
    }
  },
  computed: {
    start () {
      return this.from ?
        Math.max(moment.utc(this.from, dateFormat).unix(), this.min) : this.min
    },
    end () {
      return this.to ?
        Math.min(moment.utc(this.to, dateFormat).unix(), this.max) : this.max
    },
    dotOptions () {
      return {
        min: this.start,
        max: this.end,
        focusStyle: {
          'box-shadow': '0px 0px 1px 2px rgba(255, 152, 0, 0.36)'
        }
      }
    },
    process () {
    	return ([pos]) => {
        return [
          [this.percent(this.start), pos],
          [0, this.percent(this.start), { backgroundColor: '#999' }],
          [this.percent(this.end), 100, { backgroundColor: '#999' }]
        ]
      }
    },
    currentDate () {
      return this.toDate(this.current)
    },
    icon () { return this.play ? 'pause' : 'play' },
    ticks () {
      const ticks = {}
      const style = {
        width: '8px',
        height: '8px',
        display: 'block',
        backgroundColor: '#999',
        transform: 'translate(-2px, -2px)'
      }
      const labelStyle = {
        color: '#999',
        'font-size': '9px',
        'margin-top': '6px',
        padding: '2px'
      }

      ticks[this.min] = {
        label: (this.start - this.min > this.markMinDistance) ?
          this.toDate(this.min, 'DD.MM') : '',
        style: style,
        labelStyle: labelStyle
      }
      ticks[this.max] = {
        label: (this.max - this.end > this.markMinDistance) ?
          this.toDate(this.max, 'DD.MM') : '',
        style: style,
        labelStyle: labelStyle
      }
      ticks[this.start] = {
        label: this.toDate(this.start, 'DD.MM'),
        style: Object.assign({}, style, { backgroundColor: '#ff9800' }),
        labelStyle: Object.assign({}, labelStyle, { color: '#ff9800' })
      }
      ticks[this.end] = {
        label: this.toDate(this.end, 'DD.MM'),
        style: Object.assign({}, style, { backgroundColor: '#ff9800' }),
        labelStyle: Object.assign({}, labelStyle, { color: '#ff9800' })
      }
      this.milestones.forEach(m => {
        const d = moment.utc(m, dateFormat).unix()
        ticks[d] = {
          label: this.toDate(d, 'DD.MM'),
          style: Object.assign({}, style, { backgroundColor: 'red' }),
          labelStyle: Object.assign({}, labelStyle, { color: 'red' })
        }
      })
      return ticks
    },
    triggers () {
      return this.milestones.map(date => {
        const unix = moment.utc(date, dateFormat).unix()
        return {
          date: unix,
          position: (unix - this.min) * 100 / (this.max - this.min)
        }
      })
    }
  },
  mounted () {
    this.changed(this.current)
  },
  methods: {
    percent (value) {
      return (value - this.min) * 100 / (this.max - this.min)
    },
    toDate (timestamp, format = dateFormat) {
      return moment.unix(timestamp).format(format)
    },
    togglePlay () {
      this.play = !this.play
      if (this.play) {
        if (this.current === this.end) {
          this.current = this.start
        }
        setTimeout(() => {
          this.run = setInterval(() => {
            if (this.current < this.end) {
              this.current = Number(this.current) + this.runstep
              this.changed(this.current)
            } else {
              this.stop()
            }
          }, 250)
        }, 500)
      } else {
        this.stop()
      }
    },
    stop () {
      clearInterval(this.run)
      this.run = null
      this.play = false
    },
    changed (value) {
      this.$store.dispatch('timebar', value)
    }
  }
}

</script>

<style lang="stylus">

#timebar
  width: 100vw
  position: absolute
  left: 0
  bottom: 0
  z-index: 1003
  transition: 0.35s
  border-top: 1px solid #7a7a7a
  > div
    position: relative
    display: flex
    margin-right: 10px
    .controls
      width: 50px
      flex: 0 0 50px
      .icon
        width: 50px
        height: 50px
        text-align: center
        i
          font-size: 32px
    .bar
      width: 100%
      position: relative
      padding: 16px 0
      .vue-slider-dot-tooltip-inner-top::after
        border-top-color: #ff9800

@media (min-width: 768px)
  #timebar
    width: 350px


</style>
